const STORAGE_KEY = 'hsBundle';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

const formatEta = (eta) => {
  if (!eta) return '';
  const [year, month, day] = eta.split('-');
  return `${MONTHS[Number(month) - 1]} ${Number(day)}, ${year}`;
};

const sized = (src, width) => `${src}${src.includes('?') ? '&' : '?'}width=${width}`;

const fetchWithTimeout = (url, options = {}, ms = 15000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timer));
};

const readBundle = () => {
  try {
    const items = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
};

const isLebanonMarket = () => {
  const country = String(window.Shopify?.country || '').toUpperCase();
  return !country || country === 'LB';
};

class HsBundleBuilder extends HTMLElement {
  bundle = readBundle();
  preorders = {};

  connectedCallback() {
    this.config = JSON.parse(this.querySelector('[data-hs-bb-config]').textContent);
    this.root = window.Shopify?.routes?.root || '/';
    this.currency = window.Shopify?.currency?.active || 'USD';
    this.tray = this.querySelector('[data-tray]');
    this.quickView = this.querySelector('[data-modal="quick-view"]');
    this.sizeGuide = this.querySelector('[data-modal="size-guide"]');

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    window.addEventListener('hashchange', this.#showCategory);
    window.addEventListener('pageshow', this.#onPageShow);

    this.#showCategory();
    this.#render();
    for (const select of this.querySelectorAll('.hs-bb__card:has([data-opts]) [data-variant-select]')) this.#syncCardButton(select);
    this.#loadPreorders();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    window.removeEventListener('hashchange', this.#showCategory);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: this.currency,
    }).format(cents / 100);
  }

  #onPageShow = () => {
    this.bundle = readBundle();
    this.#resetCheckout();
    this.#render();
  };

  #onClick = (event) => {
    const target = event.target;
    const tab = target.closest('[data-tab]');
    if (tab) {
      event.preventDefault();
      history.pushState(null, '', `#${tab.dataset.tab}`);
      this.#showCategory();
      return;
    }

    const quickView = target.closest('[data-quick-view]');
    if (quickView) return this.#openQuickView(quickView.dataset.quickView);

    const sizeGuide = target.closest('[data-size-guide]');
    if (sizeGuide) return this.#openSizeGuide(sizeGuide.dataset.sizeGuide);

    if (target.closest('[data-close]')) return target.closest('dialog')?.close();
    if (target.matches('dialog')) {
      const rect = target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) target.close();
      return;
    }

    const add = target.closest('[data-add]');
    if (add) return this.#addFromCard(add.closest('[data-card]'));

    const chip = target.closest('[data-opt]');
    if (chip) return this.#pickCardOption(chip);

    const remove = target.closest('[data-remove]');
    if (remove) return this.#remove(Number(remove.dataset.remove));

    if (target.closest('[data-qv-add]')) return this.#addFromQuickView();

    const nav = target.closest('[data-qv-nav]');
    if (nav) {
      const total = this.product.images.length;
      return this.#scrollQuickViewTo((this.#quickViewIndex() + Number(nav.dataset.qvNav) + total) % total);
    }

    const value = target.closest('[data-option-value]');
    if (value) return this.#selectOption(value);

    if (target.closest('[data-checkout]')) this.#checkout();
  };

  #onChange = (event) => {
    if (event.target.matches('[data-variant-select]')) this.#syncCardButton(event.target);
  };

  #showCategory = () => {
    const tabs = [...this.querySelectorAll('[data-tab]')];
    if (!tabs.length) return;
    const hash = decodeURIComponent(window.location.hash.slice(1));
    const active = tabs.find((tab) => tab.dataset.tab === hash) || tabs[0];

    let index = 0;
    for (const card of this.querySelectorAll('[data-card]')) {
      const visible = card.dataset.type === active.dataset.tab;
      card.hidden = !visible;
      if (visible) card.style.setProperty('--hs-bb-i', String(Math.min(index++, 11)));
    }
    for (const tab of tabs) {
      if (tab === active) tab.setAttribute('aria-current', 'true');
      else tab.removeAttribute('aria-current');
    }

    const scroller = active.closest('.hs-bb__tabs-scroller');
    scroller?.scrollTo({
      left: active.offsetLeft - scroller.offsetWidth / 2 + active.offsetWidth / 2,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  #save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.bundle));
    } catch {}
    this.#render();
  }

  #push(variantId, price, img) {
    this.bundle.push({ variantId: String(variantId), price: Number(price), img });
    this.#save();
  }

  #remove(index) {
    this.bundle.splice(index, 1);
    this.#save();
  }

  #addFromCard(card) {
    const select = card?.querySelector('[data-variant-select]');
    const option = select?.selectedOptions[0];
    if (!option) return;
    if (option.dataset.available === 'false' && option.dataset.preorder !== '1') return;
    this.#push(option.value, option.dataset.price, option.dataset.img);
  }

  #tierFor(count) {
    return this.config.tiers[Math.min(count, 4) - 1];
  }

  #render() {
    const { text, showOriginalPrice } = this.config;
    const count = this.bundle.length;

    this.querySelector('[data-tray-items]').innerHTML = this.bundle
      .map(
        (item, index) => `
          <li class="hs-bb__tray-item">
            <div class="hs-bb__tray-thumb">
              <img src="${escapeHtml(item.img)}" alt="" width="55" height="55" loading="lazy">
              <button type="button" class="hs-bb__tray-remove" data-remove="${index}" aria-label="Remove">&times;</button>
            </div>
            <span class="hs-bb__tray-price">${this.#money(item.price)}</span>
          </li>`
      )
      .join('');

    this.tray.classList.toggle('is-visible', count > 0);

    const tier = count ? this.#tierFor(count) : null;
    const discount = tier?.discount || 0;
    const raw = this.bundle.reduce((sum, item) => sum + item.price, 0);
    const savings = raw * (discount / 100);
    const total = this.querySelector('[data-total]');

    total.innerHTML =
      `${escapeHtml(text.total)} ` +
      (showOriginalPrice && savings > 0 ? `<span class="hs-bb__original-price">${this.#money(raw)}</span>` : '') +
      this.#money(raw - savings);
    if (count) {
      total.classList.remove('is-bumped');
      void total.offsetWidth;
      total.classList.add('is-bumped');
    }
    this.querySelector('[data-savings]').textContent = discount ? `${text.saved} ${this.#money(savings)}` : '';
    this.querySelector('[data-offer]').textContent = tier && discount ? tier.label : '';

    for (const step of this.querySelectorAll('[data-tier]')) {
      const n = Number(step.dataset.tier);
      step.classList.toggle('is-active', count > 0 && n === Math.min(count, 4));
      step.classList.toggle('is-done', n < Math.min(count, 4));
    }

    const nudge = this.querySelector('[data-nudge]');
    if (nudge) {
      const tiers = this.config.tiers;
      const nextIndex = tiers.findIndex((t, i) => i + 1 > count && t.discount > discount);
      if (nextIndex < 0) nudge.textContent = `${tier?.label || ''} unlocked. That is the biggest saving.`;
      else {
        const need = nextIndex + 1 - count;
        nudge.textContent = `Add ${need} ${count ? 'more ' : ''}${need === 1 ? 'piece' : 'pieces'} to get ${tiers[nextIndex].label}`;
      }
    }
  }

  #resetCheckout() {
    const button = this.querySelector('[data-checkout]');
    button.disabled = false;
    button.textContent = this.config.text.checkout;
  }

  async #checkout() {
    const button = this.querySelector('[data-checkout]');
    if (button.disabled || !this.bundle.length) return;
    button.disabled = true;
    button.textContent = 'Processing...';

    const code = this.#tierFor(this.bundle.length).code;
    const items = this.bundle.map((item) => ({ id: Number(item.variantId), quantity: 1 }));

    try {
      await fetchWithTimeout(`${this.root}cart/clear.js`, { method: 'POST' });
      const response = await fetchWithTimeout(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        let message =
          'Sorry, your bundle could not be added to the cart. One of the items may have just sold out — please review and try again.';
        try {
          const error = await response.json();
          if (error?.description) message = error.description;
        } catch {}
        throw new Error(message);
      }
      localStorage.removeItem(STORAGE_KEY);
      window.location.href = code
        ? `${this.root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${this.root}checkout`)}`
        : `${this.root}checkout`;
    } catch (error) {
      console.error('[HS Bundle] checkout failed:', error);
      alert(
        error?.name === 'AbortError'
          ? 'The request timed out. Please check your connection and try again.'
          : error?.message || 'Checkout failed. Please try again.'
      );
      this.#resetCheckout();
    }
  }

  #openSizeGuide(templateId) {
    const template = this.querySelector(`#${CSS.escape(templateId)}`);
    if (!template) return;
    const body = this.sizeGuide.querySelector('[data-modal-body]');
    body.replaceChildren(template.content.cloneNode(true));
    this.sizeGuide.showModal();
  }

  async #openQuickView(handle) {
    const body = this.quickView.querySelector('[data-modal-body]');
    body.innerHTML = '<div class="hs-bb__qv-message">Loading details...</div>';
    this.quickView.showModal();

    try {
      const response = await fetch(`${this.root}products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) throw new Error('Product data not found');
      this.product = await response.json();
    } catch (error) {
      console.error('[HS Bundle] quick view failed:', error);
      body.innerHTML = `
        <div class="hs-bb__qv-message">
          <p>Sorry, this product's information could not be loaded.</p>
          <button type="button" class="hs-bb__add hs-bb__checkout" data-close>Close</button>
        </div>`;
      return;
    }

    const product = this.product;
    const description = document.createElement('div');
    description.innerHTML = product.description || '';
    description.querySelectorAll('iframe, video, object, embed, img, script, style').forEach((el) => el.remove());

    const images = product.images?.length ? product.images : [];
    const initial = product.variants.find((variant) => variant.available) || product.variants[0];
    this.selectedOptions = [...initial.options];

    const options = product.options
      .map((option, position) => {
        if (option.values.length < 2 && option.name === 'Title') return '';
        const isColor = /colou?r|لون/i.test(option.name);
        const values = option.values
          .map((value) => {
            const image = isColor && product.variants.find((v) => v.options[position] === value && v.featured_image)?.featured_image.src;
            return `
              <button type="button" class="hs-bb__qv-value${image ? ' hs-bb__qv-value--photo' : ''}" data-option-value data-position="${position}" data-value="${escapeHtml(value)}"
                aria-pressed="${value === this.selectedOptions[position]}">${
                  image ? `<img src="${escapeHtml(sized(image, 200))}" alt="" loading="lazy">` : ''
                }<span>${escapeHtml(value)}</span></button>`;
          })
          .join('');
        return `
          <div class="hs-bb__qv-option" role="group" aria-label="${escapeHtml(option.name)}">
            <span class="hs-bb__qv-option-label">${escapeHtml(option.name)}</span>
            <div class="hs-bb__qv-values">${values}</div>
          </div>`;
      })
      .join('');

    body.innerHTML = `
      <div class="hs-bb__qv">
        <div class="hs-bb__qv-gallery">
          <div class="hs-bb__qv-media" data-qv-media>
            ${images.map((src, i) => `<img src="${escapeHtml(sized(src, 1000))}" alt="" data-qv-index="${i}" loading="${i ? 'lazy' : 'eager'}">`).join('')}
          </div>
          ${
            images.length > 1
              ? `<button type="button" class="hs-bb__qv-nav hs-bb__qv-nav--prev" data-qv-nav="-1" aria-label="Previous image">&#8249;</button>
          <button type="button" class="hs-bb__qv-nav hs-bb__qv-nav--next" data-qv-nav="1" aria-label="Next image">&#8250;</button>
          <span class="hs-bb__qv-count" data-qv-count>1 / ${images.length}</span>`
              : ''
          }
        </div>
        <div class="hs-bb__qv-details">
          <h2 class="hs-bb__qv-title h3">${escapeHtml(product.title)}</h2>
          <div class="hs-bb__qv-price" data-qv-price></div>
          ${options}
          <button type="button" class="hs-bb__add" data-qv-add></button>
          <div class="hs-bb__qv-desc">${description.innerHTML}</div>
          <a class="hs-bb__qv-link" href="${escapeHtml(product.url)}">View full details</a>
        </div>
      </div>`;

    const media = body.querySelector('[data-qv-media]');
    const count = body.querySelector('[data-qv-count]');
    if (count) {
      media.addEventListener(
        'scroll',
        () => (count.textContent = `${this.#quickViewIndex() + 1} / ${images.length}`),
        { passive: true }
      );
    }

    this.#updateQuickView();
  }

  #quickViewIndex() {
    const media = this.quickView.querySelector('[data-qv-media]');
    return Math.round(Math.abs(media.scrollLeft) / media.clientWidth);
  }

  #selectOption(button) {
    const position = Number(button.dataset.position);
    this.selectedOptions[position] = button.dataset.value;
    for (const sibling of button.parentElement.children) {
      sibling.setAttribute('aria-pressed', String(sibling === button));
    }
    this.#updateQuickView(true);
  }

  #currentVariant() {
    return this.product?.variants.find((variant) => variant.options.every((value, i) => value === this.selectedOptions[i]));
  }

  #updateQuickView(scrollToImage = false) {
    const body = this.quickView.querySelector('[data-modal-body]');
    const variant = this.#currentVariant();
    const button = body.querySelector('[data-qv-add]');
    const price = body.querySelector('[data-qv-price]');
    if (!button) return;

    for (const value of body.querySelectorAll('[data-option-value]')) {
      const position = Number(value.dataset.position);
      const candidate = [...this.selectedOptions];
      candidate[position] = value.dataset.value;
      const match = this.product.variants.find((v) => v.options.every((o, i) => o === candidate[i]));
      value.classList.toggle('is-unavailable', !match || (!match.available && !this.#preorderFor(match.id)));
    }

    if (!variant) {
      button.disabled = true;
      button.textContent = this.config.text.sold;
      button.classList.remove('is-preorder');
      return;
    }

    price.textContent = this.#money(variant.price);
    const preorder = this.#preorderFor(variant.id);
    button.disabled = !variant.available && !preorder;
    button.classList.toggle('is-preorder', Boolean(preorder));
    button.textContent = preorder
      ? `⏱ Pre-Order – ${formatEta(preorder.eta)}`
      : variant.available
        ? this.config.text.add
        : this.config.text.sold;

    if (scrollToImage && variant.featured_image) {
      const src = variant.featured_image.src.split('?')[0].replace(/^https?:/, '');
      const index = this.product.images.findIndex((image) => image.split('?')[0].replace(/^https?:/, '') === src);
      if (index >= 0) this.#scrollQuickViewTo(index);
    }
  }

  #scrollQuickViewTo(index) {
    const media = this.quickView.querySelector('[data-qv-media]');
    const image = media?.querySelector(`[data-qv-index="${index}"]`);
    if (!image) return;
    media.scrollTo({ left: image.offsetLeft - media.querySelector('img').offsetLeft, behavior: 'smooth' });
  }

  #addFromQuickView() {
    const variant = this.#currentVariant();
    if (!variant || (!variant.available && !this.#preorderFor(variant.id))) return;
    const image = variant.featured_image?.src || this.product.featured_image || '';
    this.#push(variant.id, variant.price, image && sized(image, 200));
    this.quickView.close();
  }

  #preorderFor(variantId) {
    const preorder = this.preorders[String(variantId)];
    if (!preorder) return null;
    if (preorder.inventory !== null && preorder.inventory !== undefined && preorder.inventory > 0) return null;
    return preorder;
  }

  async #loadPreorders() {
    const api = window.__HS_PREORDER_API;
    if (!api || !isLebanonMarket()) return;
    try {
      const response = await fetch(api, { cache: 'no-store' });
      this.preorders = (await response.json()).variants || {};
    } catch (error) {
      console.warn('[HS Bundle PO] API fetch failed:', error);
      return;
    }
    if (!Object.keys(this.preorders).length) return;

    for (const select of this.querySelectorAll('[data-variant-select]')) {
      for (const option of select.options) {
        const preorder = this.#preorderFor(option.value);
        if (!preorder) continue;
        option.dataset.preorder = '1';
        option.dataset.preorderEta = preorder.eta;
        option.textContent = `${option.dataset.label} • Pre-Order ${formatEta(preorder.eta)}`;
      }
      this.#syncCardButton(select);
    }
    if (this.quickView.open) this.#updateQuickView();
  }

  #cardState(card) {
    return [...card.querySelectorAll('.hs-bb__opt')].map(
      (group) => group.querySelector('[aria-pressed="true"]')?.dataset.val ?? ''
    );
  }

  // The chips drive the hidden select, which stays the single source of truth for add, price and pre-order state
  #pickCardOption(chip) {
    const card = chip.closest('[data-card]');
    const select = card?.querySelector('[data-variant-select]');
    if (!select) return;
    for (const sibling of chip.parentElement.children) sibling.setAttribute('aria-pressed', String(sibling === chip));
    const key = this.#cardState(card).join('||');
    const match = [...select.options].find((option) => option.dataset.o === key);
    if (match) select.value = match.value;
    this.#syncCardButton(select, !match, true);
  }

  #syncCardButton(select, missing = false, swapImage = false) {
    const option = select.selectedOptions[0];
    const card = select.closest('[data-card]');
    const button = card?.querySelector('[data-add]');
    if (!option || !button) return;

    const state = this.#cardState(card);
    for (const chip of card.querySelectorAll('[data-opt]')) {
      const candidate = [...state];
      candidate[Number(chip.dataset.opt)] = chip.dataset.val;
      const key = candidate.join('||');
      const found = [...select.options].find((o) => o.dataset.o === key);
      chip.classList.toggle('is-unavailable', !found || (found.dataset.available === 'false' && found.dataset.preorder !== '1'));
    }
    if (missing) {
      button.disabled = true;
      button.classList.remove('is-preorder');
      button.textContent = this.config.text.sold;
      return;
    }
    const price = card.querySelector('[data-card-price]');
    if (price) price.textContent = this.#money(Number(option.dataset.price));
    const image = card.querySelector('[data-card-image]');
    if (swapImage && image && option.dataset.imgLg && image.dataset.shown !== option.dataset.imgLg) {
      image.dataset.shown = option.dataset.imgLg;
      image.removeAttribute('srcset');
      image.src = option.dataset.imgLg;
    }
    const preorder = option.dataset.preorder === '1';
    const soldOut = option.dataset.available === 'false' && !preorder;
    button.disabled = soldOut;
    button.classList.toggle('is-preorder', preorder);
    button.textContent = preorder
      ? `⏱ Pre-Order – ${formatEta(option.dataset.preorderEta)}`
      : soldOut
        ? this.config.text.sold
        : this.config.text.add;
  }
}

if (!customElements.get('hs-bundle-builder')) customElements.define('hs-bundle-builder', HsBundleBuilder);
